/**
 * Site-wide "current sport" context.
 *
 * The sport picker lives inside Landing.jsx but the sport also drives site
 * chrome (Header brand, Footer brand, browser tab title) which is mounted
 * outside the Landing route. This context is the single source of truth so
 * changing the picker instantly re-renders the chrome.
 *
 * Initial value: the first path segment (`/basketball`) in the URL wins, then
 * hostname subdomain, then the saved-filter cookie, then DEFAULT_SPORT (Basketball).
 */
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { getSavedFilters } from "@/lib/filterCookies";
import { DEFAULT_SPORT } from "@/lib/sports";
import { sportFromSlug, sportFromSubdomain } from "@/lib/sportRoutes";

const SportContext = createContext(null);

function resolveInitialSport(pathname) {
    const fromSlug = sportFromSlug(pathname.split("/")[1]);
    if (fromSlug) return fromSlug;
    const fromSub = sportFromSubdomain();
    if (fromSub) return fromSub;
    const saved = getSavedFilters({ sport: DEFAULT_SPORT });
    return saved.sport || DEFAULT_SPORT;
}

export function SportProvider({ children }) {
    const { pathname } = useLocation();
    const [sport, setSport] = useState(() => resolveInitialSport(pathname));

    // When the route changes to a /:sport page, sync context to it.
    useEffect(() => {
        const fromSlug = sportFromSlug(pathname.split("/")[1]);
        if (fromSlug && fromSlug !== sport) setSport(fromSlug);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    const value = useMemo(() => ({ sport, setSport }), [sport]);
    return <SportContext.Provider value={value}>{children}</SportContext.Provider>;
}

export function useSport() {
    const ctx = useContext(SportContext);
    // If a component is rendered outside the provider (unusual), fall back to defaults.
    if (!ctx) return { sport: DEFAULT_SPORT, setSport: () => {} };
    return ctx;
}
